import { fetchJSON } from "../../framework/api";
import * as Popup from "../../framework/components/popup";
import * as Dropdown from "../../framework/components/dropdown";
import * as Toast from "../../framework/components/toast";
import { applyViewConfig, getViewColumns, getVisibleDataColumns } from "../view_state";

/**
 * @param {Object} options
 *
 * @returns {import("../datagrid").Plugin}
 */
const DEFAULT_URLS = {
  create: () => p`/ajax/datagrid_view_config`,
  update: (id) => p`/ajax/datagrid_view_config/${id}`,
  delete: (id) => p`/ajax/datagrid_view_config/${id}`,
};

/**
 * @param {Object} [options]
 * @param {Object} [options.urls] - URL builders for each action. Each is a function
 *   returning a `p`-tagged URL so the ESLint verified-routes rule can validate them.
 */
export function storedViewsPlugin({ urls = DEFAULT_URLS } = {}) {
  return ($container) => {
    const $createBtn = $container.querySelector(".js-datagrid-view-save");
    // The views dropdown is not rendered when the academy plan lacks the datagrid_views feature.
    if (!$createBtn) return { id: "views" };

    const $createPopup = $container.querySelector("#datagrid-view-create-form-modal");
    const $createPopupForm = $container.querySelector("#datagrid-view-create-form");

    const $editPopup = $container.querySelector("#datagrid-view-edit-form-modal");
    const $editPopupForm = $container.querySelector("#datagrid-view-edit-form");

    const $viewTemplate = $container.querySelector(".js-tpl-datagrid-view");

    const $dropdown = $container.querySelector("#datagrid-views-dropdown");

    return {
      id: "views",

      onDataGridCreated() {
        const dataGrid = this;

        // Create dictionary to translate raw filter names
        const filterBuilder = dataGrid.option("filterBuilder") || {};
        const filterOperationDescriptions = filterBuilder.filterOperationDescriptions || {};

        // Wire event handlers on a single view item (used for both initial items and newly created ones)
        const wireViewItem = ($item) => {
          $item.querySelector(".js-datagrid-view-apply").addEventListener("click", () => {
            const viewConfig = JSON.parse($item.dataset.viewConfig);
            applyViewConfig(dataGrid, viewConfig.columns);
            Dropdown.toggle($dropdown, false);
          });

          const $editBtn = $item.querySelector(".js-datagrid-view-edit");
          if ($editBtn) {
            $editBtn.addEventListener("click", () => {
              const viewConfig = JSON.parse($item.dataset.viewConfig);

              // Older views may hold columns the grid no longer has (removed fields, group expanders
              // saved before command columns were filtered out): they cannot be described.
              const knownColumns = viewConfig.columns.filter((col) => dataGrid.columnOption(col.dataField));
              const columnsWithMetadata = knownColumns.map((col) => {
                const gridColumn = dataGrid.columnOption(col.dataField);
                return {
                  ...col,
                  caption: gridColumn.caption,
                  dataType: gridColumn.dataType,
                  defaultFilterOperation: gridColumn.defaultFilterOperation,
                  trueText: gridColumn.trueText,
                  falseText: gridColumn.falseText,
                };
              });
              const savedInfo = buildColumnInformation(columnsWithMetadata, filterOperationDescriptions);
              const currentInfo = buildColumnInformation(getVisibleDataColumns(dataGrid), filterOperationDescriptions);

              setupPopup($editPopup, { name: viewConfig.name, isPublic: viewConfig.public }, savedInfo);
              $editPopup.setAttribute("data-view-id", $item.dataset.viewId);

              // Toggle badge sections between saved and current grid state
              const $updateColumns = $editPopup.querySelector(".js-datagrid-form-update-columns-field");
              if ($updateColumns) {
                $updateColumns.onchange = () => {
                  updateBadgeSections($editPopup, $updateColumns.checked ? currentInfo : savedInfo);
                };
              }

              Popup.open($editPopup);
            });
          }

          const $deleteBtn = $item.querySelector(".js-datagrid-view-delete");
          if ($deleteBtn) {
            $deleteBtn.addEventListener("click", async () => {
              if (!(await Popup.confirm("#datagrid-view-delete-popup"))) return;

              fetchJSON(urls.delete($item.dataset.viewId), { method: "DELETE" }).then((data) => {
                if (data.success) {
                  $item.remove();
                  Toast.show("#datagrid-view-delete-toast");
                }
              });
            });
          }
        };

        // Wire all server-rendered view items
        $container.querySelectorAll(".js-datagrid-view").forEach(wireViewItem);

        // Configure create view button
        $createBtn.addEventListener("click", () => {
          const visibleColumns = getVisibleDataColumns(dataGrid);
          const columnInformation = buildColumnInformation(visibleColumns, filterOperationDescriptions);

          setupPopup($createPopup, { name: "", isPublic: false }, columnInformation);
          $createPopup.querySelector('button[form="datagrid-view-create-form"]').disabled = true;
          Popup.open($createPopup);
        });

        // Configure submit button of the create form
        $createPopupForm.addEventListener("submit", (event) => {
          event.preventDefault();

          const columnConfig = getViewColumns(dataGrid);
          const name = $createPopupForm.querySelector(".js-datagrid-form-name-field").value;
          const isPublic = $createPopupForm.querySelector(".js-datagrid-form-public-field").checked;

          fetchJSON(urls.create(), {
            method: "POST",
            body: {
              name,
              public: isPublic,
              viewType: $createBtn.dataset.viewType,
              columns: columnConfig,
            },
          }).then((data) => {
            if (data.success) {
              const viewConfig = { name, public: isPublic, columns: columnConfig };

              // Clone template and populate the new view item
              const $item = $viewTemplate.content.cloneNode(true).firstElementChild;
              $item.dataset.viewId = data.data.id;
              $item.dataset.viewConfig = JSON.stringify(viewConfig);
              $item.querySelector(".js-datagrid-view-name").textContent = name;
              $item.title = name;

              if (isPublic) {
                const subtitleTpl = $item.querySelector(".js-datagrid-view-subtitle-template");
                if (subtitleTpl) subtitleTpl.parentNode.insertBefore(subtitleTpl.content.cloneNode(true), subtitleTpl);
              }

              wireViewItem($item);

              // Insert alphabetically into the private views list
              const $list = $container.querySelector(".js-datagrid-user-views");
              insertAlphabetically($list, $item);

              // Remove "no views" message if present
              const $emptyMsg = $list.querySelector("p:not(.title-3)");
              if ($emptyMsg && $list.querySelectorAll(".js-datagrid-view").length > 0) $emptyMsg.remove();

              Popup.close($createPopup);
              Toast.show("#datagrid-view-success-toast");
            } else {
              Toast.show("#datagrid-view-error-toast");
            }
          });
        });

        // Configure submit button of the edit form
        $editPopupForm.addEventListener("submit", (event) => {
          event.preventDefault();

          const name = $editPopupForm.querySelector(".js-datagrid-form-name-field").value;
          const isPublic = $editPopupForm.querySelector(".js-datagrid-form-public-field").checked;
          const $updateColumnsField = $editPopupForm.querySelector(".js-datagrid-form-update-columns-field");
          const updateColumns = $updateColumnsField && $updateColumnsField.checked;

          const body = { name, public: isPublic };
          if (updateColumns) body.columns = getViewColumns(dataGrid);

          fetchJSON(urls.update($editPopup.dataset.viewId), {
            method: "PUT",
            body,
          }).then((data) => {
            if (data.success) {
              const viewId = data.updated_id;

              const viewContainer = $container.querySelector(`.js-datagrid-view[data-view-id="${viewId}"]`);
              if (!viewContainer) return;

              const applyButton = viewContainer.querySelector(".js-datagrid-view-apply");
              applyButton.title = name;
              applyButton.querySelector(".js-datagrid-view-name").textContent = name;

              // Update the public indicator
              const existingSubtitle = applyButton.querySelector(".js-datagrid-view-subtitle");
              const subtitleTemplate = applyButton.querySelector(".js-datagrid-view-subtitle-template");
              if (isPublic && !existingSubtitle) {
                const clone = subtitleTemplate.content.cloneNode(true);
                subtitleTemplate.parentNode.insertBefore(clone, subtitleTemplate);
              } else if (!isPublic && existingSubtitle) {
                existingSubtitle.remove();
              }

              // Update data-view-config to keep it in sync
              const viewConfig = JSON.parse(viewContainer.dataset.viewConfig);
              viewConfig.name = name;
              viewConfig.public = isPublic;
              if (updateColumns) viewConfig.columns = body.columns;
              viewContainer.dataset.viewConfig = JSON.stringify(viewConfig);

              sortViewsAlphabetically($container.querySelector(".js-datagrid-user-views"));

              Popup.close($editPopup);
              Toast.show("#datagrid-view-success-toast");
            } else {
              Toast.show("#datagrid-view-error-toast");
            }
          });
        });

        // Setup input validation for both forms
        setupEmptyInputValidation($createPopup, 'button[form="datagrid-view-create-form"]');
        setupEmptyInputValidation($editPopup, 'button[form="datagrid-view-edit-form"]');
      },
    };
  };
}

/**
 * Mapping of filter operation symbols to their equivalent keys in filterOperationDescriptions.
 */
const FILTER_OPERATION_EQUIVALENTS = {
  "=": "equal",
  "<>": "notEqual",
  "<": "lessThan",
  ">": "greaterThan",
  "<=": "lessThanOrEqual",
  ">=": "greaterThanOrEqual",
  notcontains: "notContains",
  startswith: "startsWith",
  endswith: "endsWith",
  isblank: "isBlank",
  isnotblank: "isNotBlank",
};

/**
 * Builds column and filter information for display in popups.
 * @param {Array} columns - Array of column objects with caption, filterValue, etc.
 * @param {Object} filterOperationDescriptions - Object mapping filter operation keys to human-readable names
 * @returns {{ columns: string[], filters: string[] }}
 */
function buildColumnInformation(columns, filterOperationDescriptions = {}) {
  const result = { columns: [], filters: [] };

  for (const col of columns) {
    result.columns.push(col.caption);

    // filters from filter row
    if (col.filterValue) {
      const operation = col.selectedFilterOperation || col.defaultFilterOperation;
      const operationKey = FILTER_OPERATION_EQUIVALENTS[operation] || operation;
      const operationLabel = (filterOperationDescriptions[operationKey] || operation).toLowerCase();

      let filterLabel;
      switch (col.dataType) {
        case "boolean":
          filterLabel = col.filterValue ? col.trueText : col.falseText;
          break;

        case "date":
          filterLabel = formatDate(col.filterValue);
          break;

        default:
          filterLabel = col.filterValue;
      }

      result.filters.push(`${col.caption} ${operationLabel} ${filterLabel}`);
    }

    // filters from filter headers
    if (col.filterValues) {
      result.filters.push(`${col.caption}`);
    }
  }

  return result;
}

/**
 * Formats a date value or date range into a localized string.
 * @param {string|Date|Array<string|Date>} dateValue - A single date value or an array of two dates representing a range
 * @returns {string} The formatted date string, or a range string (e.g., "01/01/24 - 31/12/24") if given an array
 */
function formatDate(dateValue) {
  const formatter = Intl.DateTimeFormat(document.querySelector("html").lang ?? "fr", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  const formatDate = (value) => {
    const date = new Date(value);
    return isNaN(date) ? value : formatter.format(date);
  };

  if (Array.isArray(dateValue)) {
    const [from, to] = dateValue;
    return `${formatDate(from)} - ${formatDate(to)}`;
  } else {
    return formatDate(dateValue);
  }
}

/**
 * Disables submit button when the name input is empty.
 * @param {HTMLElement} popup - The popup container
 * @param {string} submitSelector - CSS selector for the submit button
 */
function setupEmptyInputValidation(popup, submitSelector) {
  const $input = popup.querySelector(".js-datagrid-form-name-field");
  const $submit = popup.querySelector(submitSelector);
  $input.addEventListener("input", () => {
    $submit.disabled = $input.value.trim() === "";
  });
}

/**
 * Sets up the popup with name and badge information.
 * @param {HTMLElement} popup - The popup element
 * @param {{ name: string, isPublic: boolean }} formValues - The form values to populate
 * @param {{ columns: string[], filters: string[] }} badges - Column and filter information
 */
function setupPopup(popup, { name, isPublic }, badgeInfo) {
  popup.querySelector(".js-datagrid-form-name-field").value = name;
  popup.querySelector(".js-datagrid-form-public-field").checked = isPublic;

  const $updateColumns = popup.querySelector(".js-datagrid-form-update-columns-field");
  if ($updateColumns) $updateColumns.checked = false;

  updateBadgeSections(popup, badgeInfo);
}

function updateBadgeSections(popup, { columns, filters }) {
  const columnBadgesContainer = popup.querySelector(".js-datagrid-view-column-badges");
  popup.querySelector(".js-datagrid-view-column-count").innerText = columns.length;
  columnBadgesContainer.replaceChildren(createBadgeFragment(columns, columnBadgesContainer.dataset.emptyMessage));

  const filterBadgesContainer = popup.querySelector(".js-datagrid-view-filter-badges");
  popup.querySelector(".js-datagrid-view-filter-count").innerText = filters.length;
  filterBadgesContainer.replaceChildren(createBadgeFragment(filters, filterBadgesContainer.dataset.emptyMessage));
}

/**
 * Creates a DocumentFragment containing badge elements or an empty message.
 * @param {string[]} items - Array of badge labels
 * @param {string} emptyMessage - Message to display when items is empty
 * @returns {DocumentFragment}
 */
function createBadgeFragment(items, emptyMessage) {
  const fragment = document.createDocumentFragment();

  if (items.length === 0) {
    const text = document.createElement("p");
    text.innerText = emptyMessage;
    fragment.appendChild(text);
  } else {
    for (const caption of items) {
      const badge = document.createElement("span");
      badge.classList.add("badge", "badge--xs", "has-bg-purple-40");
      badge.innerText = caption;
      fragment.appendChild(badge);
    }
  }

  return fragment;
}

function sortViewsAlphabetically($list) {
  const items = $list?.querySelectorAll(".js-datagrid-view");
  if (!items || items.length <= 1) return;

  const sorted = Array.from(items).sort((a, b) => {
    const textA = a.querySelector(".js-datagrid-view-apply").innerText;
    const textB = b.querySelector(".js-datagrid-view-apply").innerText;
    return textA.localeCompare(textB);
  });
  sorted.forEach((view) => $list.appendChild(view));
}

function insertAlphabetically($list, $item) {
  $list.appendChild($item);
  sortViewsAlphabetically($list);
}
